/* global google */
import axios from "axios";
import { baseUrl } from "../constants/Config";

async function waitForGoogleMaps() {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      return resolve(window.google.maps);
    }

    // Check every 100ms until it's loaded
    const interval = setInterval(() => {
      if (window.google && window.google.maps) {
        clearInterval(interval);
        resolve(window.google.maps);
      }
    }, 100);

    // Timeout after 10 seconds to prevent hanging forever
    setTimeout(() => {
      clearInterval(interval);
      reject(new Error("Google Maps JavaScript API not loaded after 10s"));
    }, 10000);
  });
}

/**
 * Executes the Google Geocoding API call and resolves to a position object.
 * @param {string} encodedAddress The URI-encoded address string to geocode.
 * @returns {Promise<{lat: number, lng: number}>} The latitude and longitude.
 */ 
async function executeGeocode(encodedAddress) {
  await waitForGoogleMaps();

  const geocoder = new google.maps.Geocoder();

  const geocodeResult = await new Promise((resolve, reject) => {
    geocoder.geocode({ address: encodedAddress }, (results, status) => {
      if (status === "OK" && results.length > 0) {
        resolve(results[0]);
      } else {
        reject(new Error("Geocode failed: " + status));
      }
    });
  });

  if (geocodeResult?.geometry?.location) {
    const location = geocodeResult.geometry.location;
    return {
      lat: location.lat(),
      lng: location.lng(),
    };
  }

  throw new Error("Geocode successful but no location data found.");
}

/**
 * Handles the special case where only a US state is provided (address is empty).
 * @param {object} filter The filter object.
 * @returns {Promise<{currentPosition: {lat: number, lng: number}, agencyList: Array}>}
 */
async function handleStateFallback(filter) {
  const usState = filter.usState || "AZ";

  const stateToCityMap = {
    AZ: "Phoenix, AZ, USA",
    CO: "Denver, CO, USA",
    GA: "Georgia, GA, USA",
    MI: "Michigan, MI, USA",
    NM: "Albuquerque, NM, USA",
    NV: "Las Vegas, NV, USA",
    OH: "Ohio, OH, USA",
    OR: "Oregon, OR, USA",
    PA: "Philadelphia, PA, USA",
    TN: "Tennessee, TN, USA",
    TX: "Texas, TX, USA",
  };
  const cityAddress = stateToCityMap[usState] || "Phoenix, AZ, USA";

  let currentPosition = { lat: 0, lng: 0 };
  let agencyList = [];

  try {
    // 1. Get initial coordinates from geocoding the default city
    currentPosition = await executeGeocode(cityAddress);
  } catch (error) {
    console.warn(
      "Initial state geocode failed, using default (0,0):",
      error.message
    );
  }

  // 2. Fetch actual service locations for the state from the backend
 try {
   const { data, status } = await axios.get(
     `${baseUrl}api/ProviderFinder/locationstate/${usState}`,
     {
       params: {
         requestingApplicationName: filter.applicationName,
       },
     }
   );
   if (status === 200 && Array.isArray(data) && data.length > 0) {
     currentPosition = {
       lat: data[0].latitude,
       lng: data[0].longitude,
     };
     agencyList = data;
   }
 } catch (error) {
   console.warn(
     "Failed to fetch service locations for state, using fallback values:",
     error.message
   );
 }

  console.log("useUSState: true", { currentPosition, agencyList });
  return { currentPosition, agencyList };
}

/**
 * Fetches nearby agencies based on geographical coordinates.
 * @param {{lat: number, lng: number}} currentPosition The coordinates.
 * @param {string} applicationName The requesting application name.
 * @returns {Promise<{currentPosition: {lat: number, lng: number}, agencyList: Array}>}
 */
async function fetchNearbyAgencies(currentPosition, applicationName) {
   let agencyList = [];
  try {
    const { data: agencyData, status: agencyStatus } = await axios.get(
      `${baseUrl}api/ProviderFinder/${currentPosition.lat}/${currentPosition.lng}/30?requestingApplicationName=${applicationName}`
    );
    if (agencyStatus === 200) {
      agencyList = agencyData;
    }
  } catch (error) {
    console.warn(
      "Failed to fetch nearby agencies:",
      error.message
    );
  }

  console.log("useUSState: false", { currentPosition, agencyList });
  return { currentPosition, agencyList };
}

/**
 * Geocodes an address or US state and fetches nearby agencies.
 * @param {Object} filter - The filter object.
 * @param {string} filter.address - The address to geocode. If empty, uses US state fallback.
 * @param {string} filter.usState - The US state abbreviation (e.g., "AZ"). Used if address is empty.
 * @param {string} filter.applicationName - The requesting application name.
 * @returns {Promise<{currentPosition: {lat: number, lng: number}, agencyList: Array}>} 
 *          Resolves with an object containing the geocoded position and a list of agencies.
 * @throws {Error} If geocoding or agency fetching fails, logs the error and returns default values.
 */
export async function geoCodeAddress(filter) {
  if (!filter) return;

  const { address, applicationName } = filter;
  const useUSState = address === "";

  try {
    if (useUSState) {
      return await handleStateFallback(filter);
    }

    const encodedAddress = encodeURI(address);
    const currentPosition = await executeGeocode(encodedAddress);

    return await fetchNearbyAgencies(currentPosition, applicationName);
  } catch (error) {  
    console.error("Geocoding error:", error);
    return { currentPosition: { lat: 0, lng: 0 }, agencyList: [] };
  }
}
